/**
 * How the light preview resolves an import (light-preview plan F2).
 *
 * The compiler runs in the tab and has no `node_modules`: the project's own files come from
 * `workbenchStore.files`, and every bare import is left for the page's import map to resolve
 * against the prebuilt vendor bundle (`scripts/build-preview-vendor.mjs`).
 *
 * Kept free of esbuild and of the store so it stays unit-testable in the node test env.
 */

export type PreviewFiles = Readonly<Record<string, string>>;

export type ResolvedImport =
  /** A file in the project; `path` is its key in the file map. */
  | { kind: 'file'; path: string }
  /** A package: the import map points it at the vendor bundle. */
  | { kind: 'external'; spec: string }
  /** Neither — the compiler reports it instead of failing with esbuild's own message. */
  | { kind: 'missing'; spec: string };

/** The template's alias for `src/` (`tsconfig.json` and `vite.config.ts` both declare it). */
const SRC_ALIAS = '@/';

const EXTENSIONS = ['.tsx', '.ts', '.jsx', '.js', '.mjs', '.json', '.css'];

const normalize = (path: string): string => path.replace(/\\/g, '/').replace(/^\.\//, '');

/** `src/pages/../lib/x` → `src/lib/x`; keeps the result relative like the store's keys. */
function flatten(path: string): string {
  const parts: string[] = [];
  for (const segment of normalize(path).split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      parts.pop();
      continue;
    }
    parts.push(segment);
  }
  return parts.join('/');
}

/** The candidates a bundler tries for one specifier, in order. */
export function resolutionCandidates(base: string): string[] {
  return [
    base,
    ...EXTENSIONS.map((extension) => `${base}${extension}`),
    ...EXTENSIONS.map((extension) => `${base}/index${extension}`),
  ];
}

export function resolveImport(
  spec: string,
  importer: string | undefined,
  files: PreviewFiles,
): ResolvedImport {
  // A package, not a path: the vendor import map answers it in the page.
  if (!spec.startsWith('.') && !spec.startsWith(SRC_ALIAS) && !spec.startsWith('/')) {
    return { kind: 'external', spec };
  }

  const base = spec.startsWith(SRC_ALIAS)
    ? flatten(`src/${spec.slice(SRC_ALIAS.length)}`)
    : spec.startsWith('/')
      ? flatten(spec)
      : flatten(`${(importer ?? 'src/main.tsx').split('/').slice(0, -1).join('/')}/${spec}`);

  const hit = resolutionCandidates(base).find((candidate) => files[candidate] !== undefined);
  return hit ? { kind: 'file', path: hit } : { kind: 'missing', spec };
}

/** esbuild's loader for a project file, by extension. */
export function loaderFor(path: string): 'tsx' | 'ts' | 'jsx' | 'js' | 'json' | 'css' {
  if (path.endsWith('.tsx')) return 'tsx';
  if (path.endsWith('.ts')) return 'ts';
  if (path.endsWith('.jsx')) return 'jsx';
  if (path.endsWith('.json')) return 'json';
  if (path.endsWith('.css')) return 'css';
  return 'js';
}

/**
 * The package an import belongs to (`@scope/name/sub` → `@scope/name`), which is what the import
 * map is keyed by and what an "unsupported package" message should name.
 */
export function packageNameOf(spec: string): string {
  const parts = spec.split('/');
  return spec.startsWith('@') ? parts.slice(0, 2).join('/') : parts[0];
}

/** True when the vendor bundle can serve this import: the exact entry, or its package prefix. */
export function isVendored(spec: string, importMap: Readonly<Record<string, string>>): boolean {
  if (importMap[spec]) return true;
  const pkg = packageNameOf(spec);
  return Boolean(importMap[pkg] || importMap[`${pkg}/`]);
}
